import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import { TID } from "../../lib/testIds";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const EMPTY = {
  id: "", name: "", provider: "",
  adapter: "gemini_via_krai_api",
  active: true,
  priority: 100,
  modes: [],
  capabilities: [],
  config: {},
  secrets: {},
  notes: "",
};

const CAP_OPTIONS = [
  "chat", "vision", "image_edit", "image_generation",
  "audio_transcription", "audio_generation", "video_generation",
  "embeddings", "tool_use", "code",
];

// Sensible defaults per adapter. Applied only on ADAPTER SWITCH (new
// engine or when the operator changes the dropdown in an existing one)
// and ONLY when the target field is empty — never overrides user input.
// Keeps the UI helpful without being magical.
const ADAPTER_DEFAULTS = {
  gemini_via_krai_api: {
    provider: "google",
    capabilities: ["chat"],
    config: { profile: "gemini" },
  },
  gemini_image_web: {
    provider: "google",
    capabilities: ["image_generation", "image_edit"],
    config: { profile: "gemini", timeout_ms: 60000 },
  },
};

export default function AddEngineDialog({ engine, adapters, onClose, onSaved }) {
  const isEdit = !!engine;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [configError, setConfigError] = useState(false);
  const [configText, setConfigText] = useState(null);

  useEffect(() => {
    setForm(engine ? { ...EMPTY, ...engine, secrets: {} } : EMPTY);
    setConfigText(null); setConfigError(false);
  }, [engine]);

  const currentAdapter = (adapters || []).find((a) => a.id === form.adapter);
  const requiredCfgKeys = currentAdapter?.required_config_keys || [];
  const requiredSecretKeys = currentAdapter?.required_secret_keys || [];

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setConfig = (k, v) => {
    setConfigText(null); setConfigError(false);
    setForm((f) => ({ ...f, config: { ...f.config, [k]: v } }));
  };
  const setSecret = (k, v) => setForm((f) => ({ ...f, secrets: { ...f.secrets, [k]: v } }));

  const onAdapterChange = (adapterId) => {
    setConfigText(null); setConfigError(false);
    setForm((f) => {
      const defaults = ADAPTER_DEFAULTS[adapterId] || {};
      const nextConfig = { ...(defaults.config || {}), ...(f.config || {}) };
      return {
        ...f,
        adapter: adapterId,
        provider: f.provider || defaults.provider || "",
        capabilities: defaults.capabilities || ["chat"],
        config: nextConfig,
      };
    });
  };

  const toggleCap = (c) => {
    setForm((f) => ({
      ...f,
      capabilities: f.capabilities.includes(c)
        ? f.capabilities.filter((x) => x !== c)
        : [...f.capabilities, c],
    }));
  };

  const submit = async () => {
    if (!isEdit && (!form.id.trim() || !form.name.trim() || !form.provider.trim())) {
      toast.error("Rellena id, nombre y provider.");
      return;
    }
    if (configError) { toast.error("Corrige la configuración JSON."); return; }
    setSaving(true);
    try {
      if (isEdit) {
        const { id, created_at, updated_at, config_secrets_enc, ...patch } = form;
        // If user did not enter new secrets, omit the field so backend keeps existing.
        if (!Object.keys(patch.secrets || {}).some((k) => (patch.secrets[k] || "").trim() !== "")) {
          delete patch.secrets;
        }
        await api.patch(`/engine-registry/${engine.id}`, patch);
        toast.success("Motor actualizado");
      } else {
        await api.post("/engine-registry", { ...form, id: form.id.trim() });
        toast.success("Motor creado");
      }
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid={TID.engines.dialog}
        className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? `Editar motor "${engine.id}"` : "Nuevo motor"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">ID interno</Label>
              <Input
                data-testid={TID.engines.fId}
                value={form.id}
                onChange={(e) => set({ id: e.target.value.toLowerCase() })}
                disabled={isEdit}
                placeholder="gemini"
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Nombre</Label>
              <Input
                data-testid={TID.engines.fName}
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
                placeholder="Google Gemini"
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Provider</Label>
              <Input
                data-testid={TID.engines.fProvider}
                value={form.provider}
                onChange={(e) => set({ provider: e.target.value.toLowerCase() })}
                placeholder="google"
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Adapter</Label>
              <Select value={form.adapter} onValueChange={onAdapterChange}>
                <SelectTrigger data-testid={TID.engines.fAdapter} className="bg-zinc-900 border-zinc-800">
                  <SelectValue placeholder="Elige adapter…" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {(adapters || []).map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {currentAdapter?.description && (
            <div className="text-[11px] text-zinc-500 leading-relaxed rounded-md border border-zinc-800 bg-zinc-900/40 p-2.5">
              {currentAdapter.description}
            </div>
          )}

          {requiredCfgKeys.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-zinc-400">Configuración</Label>
              {requiredCfgKeys.map((k) => (
                <div key={k} className="space-y-1">
                  <Label className="text-[10px] text-zinc-500 font-mono">{k}</Label>
                  <Input
                    data-testid={TID.engines.fConfig(k)}
                    value={(form.config || {})[k] || ""}
                    onChange={(e) => setConfig(k, e.target.value)}
                    className="bg-zinc-900 border-zinc-800 font-mono text-xs"
                  />
                </div>
              ))}
            </div>
          )}

          {requiredSecretKeys.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-zinc-400">
                Secretos <span className="text-[10px] text-zinc-600">(cifrados AES-GCM)</span>
              </Label>
              {requiredSecretKeys.map((k) => (
                <div key={k} className="space-y-1">
                  <Label className="text-[10px] text-zinc-500 font-mono">{k}</Label>
                  <Input
                    data-testid={TID.engines.fSecret(k)}
                    type="password"
                    value={(form.secrets || {})[k] || ""}
                    onChange={(e) => setSecret(k, e.target.value)}
                    placeholder={isEdit ? "Dejar vacío para conservar" : ""}
                    className="bg-zinc-900 border-zinc-800 font-mono text-xs"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs text-zinc-400">Capacidades</Label>
            <div className="flex flex-wrap gap-1.5" data-testid={TID.engines.fCapabilities}>
              {CAP_OPTIONS.map((c) => {
                const on = form.capabilities.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCap(c)}
                    className={
                      "text-[10px] px-2 py-1 rounded border transition-colors " +
                      (on
                        ? "border-emerald-700 bg-emerald-950/40 text-emerald-300"
                        : "border-zinc-800 text-zinc-500 hover:text-zinc-300")
                    }
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Prioridad (el número menor se intenta primero)</Label>
            <Input type="number" min={0} max={9999} value={form.priority}
              onChange={(e) => set({ priority: Number(e.target.value) })} />
            <Label>Modos de imagen permitidos</Label>
            <div className="flex gap-4">
              {["BASIC", "PRO"].map((mode) => (
                <label key={mode} className="flex gap-2 text-sm">
                  <input type="checkbox" checked={(form.modes || []).includes(mode)}
                    onChange={(e) => set({ modes: e.target.checked
                      ? [...(form.modes || []), mode] : form.modes.filter((m) => m !== mode) })} />
                  {mode}
                </label>
              ))}
            </div>
            <p className="text-xs text-zinc-400">Sin selección, el motor solo participa en solicitudes sin modo.</p>
            <Label>Configuración avanzada (JSON)</Label>
            <Textarea value={configText ?? JSON.stringify(form.config, null, 2)} rows={5}
              onChange={(e) => {
                setConfigText(e.target.value);
                try {
                  const parsed = JSON.parse(e.target.value);
                  if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") throw new Error();
                  set({ config: parsed }); setConfigError(false);
                } catch { setConfigError(true); }
              }} />
            {configError && <p role="alert" className="text-red-400">JSON inválido.</p>}
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs text-zinc-400">Activo</Label>
            <Switch
              data-testid={TID.engines.fActive}
              checked={form.active}
              onCheckedChange={(v) => set({ active: v })}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">Notas</Label>
            <Textarea
              data-testid={TID.engines.fNotes}
              value={form.notes}
              onChange={(e) => set({ notes: e.target.value })}
              rows={2}
              className="bg-zinc-900 border-zinc-800 text-sm resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
          <Button
            data-testid={TID.engines.submit}
            onClick={submit}
            disabled={saving}
            size="sm"
            className="bg-white text-black hover:bg-zinc-200"
          >
            {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear motor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

